/**
 * MOOJO — Découverte par origine : recherche, filtres et tri côté client.
 *
 * 2026-08-28 · Discovery V2.
 *
 * La barre de découverte est construite ICI, en un seul endroit. Avant, le
 * Liquid peignait quatre groupes de facettes avec leurs libellés et ce script
 * les remplissait — deux propriétaires pour un même composant, et un paramètre
 * `label` que le script recevait sans jamais s'en servir.
 *
 * Deux facettes seulement sont exposées à l'achat :
 *   Profil            — familles sensorielles, données catalogue
 *   Teneur en cacao   — custom.cacao_pct, seule autorité du pourcentage
 *
 * Retirées de la découverte primaire, conservées dans la donnée et les pages
 * de contenu :
 *   Variété      — la source de la facette (custom.type_de_cacao) n'est pas un
 *                  champ de variété : elle contient « Chocolat noir sucré »,
 *                  « Bean-to-bar », « Bâtons d'encens ». Et même nettoyée par
 *                  custom.variete_de_cacao, la dimension mélange groupes
 *                  génétiques, cultivars et classifications qualité. C'est un
 *                  savoir d'amateur, pas un critère d'achat.
 *   Fermentation — traçabilité (« 3–5 jours en caisse (centre Norandino) »).
 *                  Sa place est sur la fiche produit et la page origine.
 */

const MOOJO_DISCOVERY_SRC = document.currentScript ? document.currentScript.src : '';

/* Revision de la feuille de style. Le `?v=` du script ne change QUE si le
   script change : une modification portant sur la seule feuille resterait
   invisible derriere le cache CDN (`max-age=31557600`, mesure faite :
   `age` a depasse 24 h sur une feuille pourtant remplacee).
   A INCREMENTER A CHAQUE EDITION DE assets/moojo-discovery.css : le jeton vit
   dans ce fichier, donc le bump change aussi le `?v=` du script, et l'URL de
   la feuille devient neuve des deux cotes.
   La vraie correction est que la section emette elle-meme
   `{{ 'moojo-discovery.css' | asset_url | stylesheet_tag }}` — elle est deja
   ecrite dans moojo-origin-country.liquid en local, mais ce fichier appartient
   a la porte Colombie et n'est pas deploye. */
const MOOJO_DISCOVERY_CSS_REV = '2';

/* La barre est un composant : elle apporte sa propre feuille de style, et
   seulement sur les pages qui la rendent. */
(function ensureDiscoveryStylesheet() {
  if (!MOOJO_DISCOVERY_SRC) return;
  const parts = MOOJO_DISCOVERY_SRC.split('moojo-origin-country.js');
  const version = parts[1] || '';
  const href =
    parts[0] +
    'moojo-discovery.css' +
    version +
    (version ? '&' : '?') +
    'r=' +
    MOOJO_DISCOVERY_CSS_REV;
  if (document.querySelector('link[href*="moojo-discovery.css"]')) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = href;
  document.head.appendChild(link);
})();

/* Libellés d'interface. Le composant était entièrement écrit en français en
   dur ; on garde la même portée, en donnant au moins NL et EN une version
   correcte. À déplacer dans les fichiers de locale quand la section elle-même
   passera au `t:`. Les valeurs de facettes, elles, restent des données
   catalogue et ne se traduisent pas ici. */
const MOOJO_I18N = {
  fr: {
    profile: 'Profil',
    cacao: 'Teneur en cacao',
    all: 'Tous',
    reset: 'Tout afficher',
    refine: 'Affiner',
    countOne: '1 création',
    countMany: '{n} créations',
    emptyFilteredTitle: 'Aucune création ne correspond à cette sélection.',
    emptyFilteredBody: 'Essayez une autre combinaison.',
    emptyFilteredAction: 'Réinitialiser les filtres',
    emptySoonTitle: 'Aucune création à {v} % pour le moment.',
    emptySoonBody: 'Nous vous en préparons de nouvelles.',
    emptySoonAction: 'Voir toutes les créations',
  },
  nl: {
    profile: 'Profiel',
    cacao: 'Cacaogehalte',
    all: 'Alle',
    reset: 'Alles tonen',
    refine: 'Verfijnen',
    countOne: '1 creatie',
    countMany: '{n} creaties',
    emptyFilteredTitle: 'Geen creatie komt overeen met deze selectie.',
    emptyFilteredBody: 'Probeer een andere combinatie.',
    emptyFilteredAction: 'Filters wissen',
    emptySoonTitle: 'Voorlopig geen creatie met {v} % cacao.',
    emptySoonBody: 'We werken aan nieuwe.',
    emptySoonAction: 'Alle creaties bekijken',
  },
  en: {
    profile: 'Profile',
    cacao: 'Cacao content',
    all: 'All',
    reset: 'Show all',
    refine: 'Refine',
    countOne: '1 creation',
    countMany: '{n} creations',
    emptyFilteredTitle: 'No creation matches this selection.',
    emptyFilteredBody: 'Try another combination.',
    emptyFilteredAction: 'Clear filters',
    emptySoonTitle: 'No creation at {v} % just yet.',
    emptySoonBody: 'New ones are on the way.',
    emptySoonAction: 'See all creations',
  },
};

function moojoStrings() {
  const lang = (document.documentElement.lang || 'fr').slice(0, 2).toLowerCase();
  return MOOJO_I18N[lang] || MOOJO_I18N.fr;
}

/* Echelle canonique de teneur en cacao (univers chocolat).
   Elle ne se deduit PAS des produits de l'origine affichee : elle enseigne
   l'etendue MOOJO et donne une place ou annoncer les creations a venir.
   67, 72, 76, 82 et 86 sont portes par custom.cacao_pct. 100 % n'est plus
   une pastille de teneur : c'est l'univers rituel (onglet 100 % cacao). */
const MOOJO_CACAO_SCALE = ['67', '72', '76', '82', '86'];

/* Ordre sensoriel, pas alphabétique : du fruit au boisé. Les valeurs absentes
   de cette liste sont ajoutées ensuite, par ordre alphabétique — le catalogue
   peut évoluer sans que la barre casse. */
const MOOJO_PROFILE_ORDER = [
  'Fruité',
  'Floral',
  'Doux & Rond',
  'Caramélisé / Miel',
  'Noisetté / Praliné',
  'Crémeux',
  'Épicé doux',
  'Boisé / Terreux',
];

class MoojoOriginCatalog extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[data-origin-grid]');
    if (!this.grid) return;

    this.t = moojoStrings();
    this.cards = [...this.grid.querySelectorAll('[data-origin-card]')];
    this.searchInput = this.querySelector('[data-origin-search]');
    this.sortSelect = this.querySelector('[data-origin-sort]');
    this.filterButtons = [...this.querySelectorAll('[data-origin-filter]')];
    this.countEl = this.querySelector('[data-origin-count]');
    this.emptyEl = this.querySelector('[data-origin-empty]');
    this.facetsHost = this.querySelector('.moojo-origin__facets');
    this.leadEl = this.querySelector('[data-origin-universe-lead]');

    this.universeCopy = {
      rituel: 'Le cacao pur de ce terroir. À boire, à râper, sans sucre.',
      chocolat: 'Le même terroir, sucré et tempéré en tablette. Une lecture plus douce du même lot.',
    };

    this.availableFamilies = new Set(
      this.cards.map((card) => card.dataset.originFamily).filter(Boolean)
    );

    this.filterButtons.forEach((btn) => {
      const family = btn.dataset.originFilter;
      if (!this.availableFamilies.has(family)) {
        btn.hidden = true;
      }
    });

    const hash = (window.location.hash || '').replace('#', '').toLowerCase();
    const hashFilter = hash === 'chocolat' || hash === 'chocolate' ? 'chocolat' : 'rituel';
    this.activeFilter = this.availableFamilies.has(hashFilter)
      ? hashFilter
      : this.availableFamilies.has('rituel')
        ? 'rituel'
        : this.availableFamilies.has('chocolat')
          ? 'chocolat'
          : 'rituel';

    this.activeFacets = { notes: 'all', pct: 'all' };

    this.syncTabs();
    this.buildBar();

    this.searchInput?.addEventListener('input', () => this.apply());
    this.sortSelect?.addEventListener('change', () => this.apply());
    this.filterButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        this.activeFilter = btn.dataset.originFilter || 'rituel';
        if (this.activeFilter !== 'chocolat') this.activeFacets.pct = 'all';
        this.syncTabs();
        this.buildBar();
        this.apply();
        const nextHash = this.activeFilter === 'chocolat' ? 'chocolat' : 'cacao';
        if (history.replaceState) {
          history.replaceState(null, '', `${location.pathname}${location.search}#${nextHash}`);
        }
      });
    });
    window.addEventListener('hashchange', () => {
      const next = (window.location.hash || '').replace('#', '').toLowerCase();
      const mapped = next === 'chocolat' || next === 'chocolate' ? 'chocolat' : 'rituel';
      if (this.availableFamilies.has(mapped) && mapped !== this.activeFilter) {
        this.activeFilter = mapped;
        this.syncTabs();
        this.buildBar();
        this.apply();
      }
    });

    this.apply();
  }

  syncTabs() {
    this.filterButtons.forEach((btn) => {
      const on = btn.dataset.originFilter === this.activeFilter;
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      btn.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    if (this.leadEl) {
      this.leadEl.textContent = this.universeCopy[this.activeFilter] || '';
    }
  }

  /* ---- Construction de la barre ---- */

  collectNotes() {
    /* Le profil, lui, reste derive du catalogue affiche : une famille
       sensorielle absente de l'origine n'a rien a enseigner, contrairement a
       une teneur en cacao. */
    const notes = new Set();
    this.cards.forEach((card) => {
      (card.dataset.originNotes || '')
        .split('·')
        .map((n) => n.trim())
        .filter(Boolean)
        .forEach((n) => notes.add(n));
    });

    const ordered = [];
    MOOJO_PROFILE_ORDER.forEach((n) => {
      if (notes.has(n)) {
        ordered.push(n);
        notes.delete(n);
      }
    });
    [...notes].sort((a, b) => a.localeCompare(b, 'fr')).forEach((n) => ordered.push(n));
    return ordered;
  }

  /* Le 100 % n'est pas un metachamp : c'est la famille cacao_100, resolue en
     amont par le snippet canonique moojo-product-family et transmise a la
     carte. Un cacao_pct = 100 explicite compte aussi, s'il existe un jour.
     Aucune heuristique de famille n'est refaite ici. */
  cardMatchesPct(card, pct) {
    if (pct === 'all') return true;
    const cardPct = (card.dataset.originPct || '').trim();
    if (pct === '100') {
      return card.dataset.originFamily === 'rituel' || cardPct === '100';
    }
    return cardPct === pct;
  }

  buildBar() {
    if (!this.facetsHost) return;

    const notes = this.collectNotes();

    /* Le Liquid peut encore contenir les anciens groupes statiques (Profil,
       Intensité, Variété, Fermentation). Ils sont remplacés en bloc : ce
       composant est désormais le seul propriétaire de la barre. */
    this.facetsHost.innerHTML = '';

    /* La barre n'est plus une simple rangee de toolbar : la regle de section
       `.moojo-origin__toolbar-row { display: flex }` est portee par un
       selecteur d'ID, donc elle gagnerait sur la grille du composant et
       tasserait les deux dimensions sur une seule ligne. On retire la classe
       plutot que d'empiler des !important. */
    this.facetsHost.classList.remove('moojo-origin__toolbar-row');

    const rows = [];

    /* Le profil n'est offert que s'il offre un choix : une seule valeur n'est
       pas un filtre. */
    if (notes.length > 1) {
      rows.push(this.buildRow('notes', this.t.profile, notes.map((v) => ({ value: v, label: v }))));
    }

    /* La teneur, elle, expose toujours l'echelle MOOJO complete, meme si
       l'origine affichee ne couvre pas toute la gamme. Un 86 % clique sur une
       origine qui n'en propose pas encore mene a un message d'attente, pas a
       une impasse : c'est la place ou annoncer les creations a venir. */
    /* La teneur n'a de sens que dans l'univers chocolat. */
    if (this.activeFacets.pct !== 'all' && !MOOJO_CACAO_SCALE.includes(this.activeFacets.pct)) {
      this.activeFacets.pct = 'all';
    }
    if (this.cards.length && this.activeFilter === 'chocolat') {
      rows.push(
        this.buildRow(
          'pct',
          this.t.cacao,
          MOOJO_CACAO_SCALE.map((v) => ({ value: v, label: v + ' %' }))
        )
      );
    }

    if (!rows.length) {
      this.facetsHost.hidden = true;
      return;
    }

    this.facetsHost.hidden = false;
    this.facetsHost.setAttribute('role', 'group');
    this.facetsHost.setAttribute('aria-label', this.t.refine);
    rows.forEach((r) => this.facetsHost.appendChild(r));

    this.resetBtn = document.createElement('button');
    this.resetBtn.type = 'button';
    this.resetBtn.className = 'moojo-discovery__reset';
    this.resetBtn.textContent = this.t.reset;
    this.resetBtn.hidden = true;
    this.resetBtn.addEventListener('click', () => this.reset());
    this.facetsHost.appendChild(this.resetBtn);
  }

  buildRow(key, label, entries) {
    const row = document.createElement('div');
    row.className = 'moojo-discovery__row';
    row.dataset.originFacetGroup = key;

    const caption = document.createElement('span');
    caption.className = 'moojo-discovery__label';
    caption.id = `moojo-facet-${key}`;
    caption.textContent = label;
    row.appendChild(caption);

    const list = document.createElement('div');
    list.className = 'moojo-discovery__list';
    list.setAttribute('role', 'group');
    list.setAttribute('aria-labelledby', caption.id);
    list.appendChild(this.makePill('all', this.t.all, key, true));
    entries.forEach((e) => list.appendChild(this.makePill(e.value, e.label, key, false)));
    row.appendChild(list);

    return row;
  }

  makePill(value, label, group, pressed) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'moojo-discovery__pill';
    btn.dataset.originFacet = group;
    btn.dataset.originFacetValue = value;
    btn.setAttribute('aria-pressed', pressed ? 'true' : 'false');
    btn.textContent = label;
    btn.addEventListener('click', () => {
      /* Recliquer la valeur active la relâche : on ne se retrouve jamais
         coincé dans un filtre sans savoir comment en sortir. */
      const next = this.activeFacets[group] === value ? 'all' : value;
      this.activeFacets[group] = next;
      this.querySelectorAll(`[data-origin-facet="${group}"]`).forEach((b) => {
        b.setAttribute('aria-pressed', b.dataset.originFacetValue === next ? 'true' : 'false');
      });
      this.apply();
    });
    return btn;
  }

  /* Deux vides differents, deux reponses differentes.

     A. Une seule dimension active, et c'est une teneur : la selection est
        legitime, l'origine ne la propose simplement pas encore. On l'annonce
        au futur plutot que de la traiter comme une erreur.
     B. Plusieurs criteres combines : c'est un cul-de-sac de filtrage, on
        propose de repartir de zero.

     Le seuil est le nombre de criteres actifs, rien de plus : pas de machine
     a etats. */
  renderEmptyState(activeCount, pct) {
    if (!this.emptyEl) return;
    this.emptyEl.classList.add('moojo-discovery__empty');
    this.emptyEl.innerHTML = '';

    const soon = activeCount === 1 && pct !== 'all';

    const title = document.createElement('p');
    title.className = 'moojo-discovery__empty-title';
    title.textContent = soon
      ? this.t.emptySoonTitle.replace('{v}', pct)
      : this.t.emptyFilteredTitle;

    const body = document.createElement('p');
    body.className = 'moojo-discovery__empty-body';
    body.textContent = soon ? this.t.emptySoonBody : this.t.emptyFilteredBody;

    const action = document.createElement('button');
    action.type = 'button';
    action.className = 'moojo-discovery__reset moojo-discovery__reset--inline';
    action.textContent = soon ? this.t.emptySoonAction : this.t.emptyFilteredAction;
    action.addEventListener('click', () => this.reset());

    this.emptyEl.append(title, body, action);
  }

  reset() {
    this.activeFilter = this.availableFamilies.has('rituel')
      ? 'rituel'
      : this.availableFamilies.has('chocolat')
        ? 'chocolat'
        : 'rituel';
    this.activeFacets = { notes: 'all', pct: 'all' };
    if (this.searchInput) this.searchInput.value = '';
    this.syncTabs();
    this.querySelectorAll('[data-origin-facet]').forEach((b) => {
      b.setAttribute('aria-pressed', b.dataset.originFacetValue === 'all' ? 'true' : 'false');
    });
    this.apply();
    this.searchInput?.focus();
  }

  /* ---- Application ---- */

  apply() {
    const query = (this.searchInput?.value || '').trim().toLowerCase();
    const sort = this.sortSelect?.value || 'featured';
    const filter = this.activeFilter;
    const { notes, pct } = this.activeFacets;

    let visible = this.cards.filter((card) => {
      const family = card.dataset.originFamily || '';
      const haystack = (card.dataset.originSearch || '').toLowerCase();
      if (filter !== 'all' && family !== filter) return false;
      if (query && !haystack.includes(query)) return false;
      if (!this.cardMatchesPct(card, pct)) return false;
      if (notes !== 'all') {
        const cardNotes = (card.dataset.originNotes || '').split('·').map((n) => n.trim());
        if (!cardNotes.includes(notes)) return false;
      }
      return true;
    });

    visible = this.sortCards(visible, sort);

    this.cards.forEach((card) => {
      card.hidden = true;
      card.style.order = '';
    });

    visible.forEach((card, index) => {
      card.hidden = false;
      card.style.order = String(index);
    });

    if (this.countEl) {
      const n = visible.length;
      this.countEl.textContent = n === 1 ? this.t.countOne : this.t.countMany.replace('{n}', String(n));
    }

    let activeCount = 0;
    if (notes !== 'all') activeCount += 1;
    if (pct !== 'all') activeCount += 1;
    if (query !== '') activeCount += 1;

    if (this.emptyEl) {
      this.emptyEl.hidden = visible.length > 0;
      if (!visible.length) this.renderEmptyState(activeCount, pct);
    }

    const touched = activeCount > 0;
    if (this.resetBtn) this.resetBtn.hidden = !touched;
    this.classList.toggle('is-filtered', touched);
  }

  sortCards(cards, mode) {
    const sorted = [...cards];
    sorted.sort((a, b) => {
      if (mode === 'price-asc') return Number(a.dataset.originPrice) - Number(b.dataset.originPrice);
      if (mode === 'price-desc') return Number(b.dataset.originPrice) - Number(a.dataset.originPrice);
      if (mode === 'name') return (a.dataset.originName || '').localeCompare(b.dataset.originName || '', 'fr');
      return Number(a.dataset.originIndex) - Number(b.dataset.originIndex);
    });
    return sorted;
  }
}

if (!customElements.get('moojo-origin-catalog')) {
  customElements.define('moojo-origin-catalog', MoojoOriginCatalog);
}

/** Scroll reveal for narrative blocks */
(function initOriginReveal() {
  const nodes = document.querySelectorAll('.moojo-origin [data-oc-reveal]');
  if (!nodes.length) return;

  if (!('IntersectionObserver' in window)) {
    nodes.forEach((el) => el.classList.add('is-revealed'));
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        }
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
  );

  nodes.forEach((el) => observer.observe(el));
})();
